import { moveAllUserRetailerShoppingCarts } from "@/api/rest/checkoutApi";
import { getChallengeCodeForMagicLinkToken } from "@/api/rest/unauthenticated";
import {
  AnalyticsService,
  AnalyticsTrackingEvent,
  getUserSignedInState,
  getVisitorIdCookie,
  setOtpAuth,
  tryGetVisitorId,
  AmplifyHub as Hub,
} from "@brandclub/common-ui";
import { useActor } from "@xstate/react";
import {
  createContext,
  ReactElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useBus } from "react-bus";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ActorLogicFrom, ActorRefFrom, SnapshotFrom } from "xstate";
import { transferVisitorActions } from "../../../api/rest/authenticated/transferVisitorActions";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { clearCustomerSpendByBrand } from "../../../redux/reducers/customerSpendByBrand";
import { clearRewards } from "../../../redux/reducers/rewards";
import { clearUserProfile } from "../../../redux/reducers/userProfile";
import { CLOSE_LOGIN_DRAWER } from "../../../utils/busEvents";
import { useTrackActions } from "../../../utils/hooks/useTracking";
import Loading from "../../Loading";
import { AuthHelper } from "./AuthHelper";
import { saveNewPhoneNumber, Status } from "./updatePhone/api";
import { userLoginMachine } from "./UserLoginMachines";
import { useSyncBrowserNavigationWithUserLoginState } from "./useSyncBrowserNavigationWithUserLoginState";

export const UserLoginContext = createContext<UserLoginContextType>(
  null as never
);

const loadCartIntoUser = async () => {
  try {
    const uniqueVisitorId = await tryGetVisitorId();
    const uniqueVisitorIdFromCookie = getVisitorIdCookie();
    const uniqueVisitorIdToUse =
      uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== ""
        ? uniqueVisitorIdFromCookie
        : uniqueVisitorId;
    if (uniqueVisitorIdToUse) {
      await moveAllUserRetailerShoppingCarts(uniqueVisitorIdToUse);
    }
  } catch (err) {
    console.error(err);
  }
};

const transferVisitorActionsToUser = async () => {
  try {
    await transferVisitorActions();
  } catch (err) {
    console.error(err);
  }
};

const MAGIC_LINK_LOGIN_PARAM_NAME = "magicState";

// Custom hook for magic link authentication
const useMagicLinkAuth = (
  send: ActorRefFrom<typeof userLoginMachine>["send"]
) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const processedRef = useRef(false);

  const magicState = searchParams.get(MAGIC_LINK_LOGIN_PARAM_NAME);

  useEffect(() => {
    (async () => {
      // Clean up URL first if magic state exists
      let magicLinkToken = null;
      if (magicState) {
        try {
          const decodedMagicState = atob(magicState); // this transforms the base64 string to a string
          const { token, expiresAt } = JSON.parse(decodedMagicState);
          if (!expiresAt || Date.now() > expiresAt) {
            magicLinkToken = null;
          } else {
            magicLinkToken = token;
          }
        } catch (error) {
          magicLinkToken = magicState;
        }
        const newParams = new URLSearchParams(searchParams);
        newParams.delete(MAGIC_LINK_LOGIN_PARAM_NAME);
        setSearchParams(newParams, { replace: true });
      }

      // Process magic link authentication only once
      if (magicLinkToken && !processedRef.current) {
        processedRef.current = true;
        setIsLoading(true);
        try {
          const { status, phoneNumber, challengeCode } =
            await getChallengeCodeForMagicLinkToken(magicLinkToken);

          if (status === "success" && phoneNumber && challengeCode) {
            await AuthHelper.loginWithCustomCode({
              phoneNumber,
              challengeCode,
              magicLinkToken,
            });
            // rerun the machine to load the user data after login
            send({ type: "RERUN_MACHINE", isMagicLinkAuth: true });
          } else {
            console.error("Magic link authentication failed");
          }
        } catch (error) {
          console.error("Error during magic link login:", error);
        } finally {
          setIsLoading(false);
        }
      }
    })();
  }, [magicState, dispatch, searchParams, setSearchParams, send]);

  return {
    isLoading,
    hasMagicState: !!magicState,
  };
};

const UserLoginProvider = ({ children }: { children: ReactElement }) => {
  const [trackAction] = useTrackActions();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const authConfig = useAppSelector(({ appConfig }) => appConfig?.authConfig);

  const domainConfig = useAppSelector(
    ({ appConfig }) => appConfig?.domainConfig
  );

  // Capture the initial search param value on first render to avoid losing it during login redirects.
  const [searchParams] = useSearchParams();
  const [ssoRedirectUrl] = useState(() => {
    const ssoUrl = searchParams.get("ssoRedirectUrl");
    if (ssoUrl) return ssoUrl;
    
    // Fall back to redirectUrl, but ignore if it's from ChatGPT
    // (ChatGPT's openExternal API appends redirectUrl=https://chatgpt.com/... to all external links)
    const redirectUrl = searchParams.get("redirectUrl");
    if (redirectUrl && redirectUrl.includes("chatgpt.com")) {
      return null;
    }
    return redirectUrl;
  });
  const [resendCoolDown, setResendCooldown] = useState(0);

  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState<string | null>(null);

  const [submittingSignIn, setSubmittingSignIn] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (resendCoolDown > 0) {
      setTimeout(() => setResendCooldown(resendCoolDown - 1), 1000);
    } else {
      setResendCooldown(0);
    }
  }, [setResendCooldown, resendCoolDown]);

  const verifyOtp = async (
    otp: string,
    otpSession?: any,
    successCallback?: () => void,
    errorCallback?: () => void
  ) => {
    setVerifyingOtp(true);
    const verifyingUserSession = otpSession || snapshot.context.cognitoSession;
    await AuthHelper.answerCustomChallenge(verifyingUserSession, otp)
      .then(async (cognitoUser) => {
        setLoadingProfile(true);
        await setOtpAuth(cognitoUser);
        successCallback?.();
      })
      .catch((err) => {
        setOtpError("Invalid code, please try again");
        console.error(err);
        errorCallback?.();
      })
      .finally(() => {
        setVerifyingOtp(false);
        setLoadingProfile(false);
      });
  };

  const clearOtpError = () => {
    setOtpError(null);
  };

  const resendOtp = async (
    phoneNumber?: string,
    isNewUser?: boolean,
    successCallback?: (cognitoSession: any) => void,
    errorCallback?: () => void
  ) => {
    setResendCooldown(30);
    try {
      setVerifyingOtp(true);
      const phoneNumberToUse = phoneNumber || snapshot.context.otpPhoneNumber;
      if (phoneNumberToUse === undefined) {
        throw new Error("Phone number is not defined");
      }
      const cognitoSession = await AuthHelper.sendAuthOtpWithSms(
        phoneNumberToUse,
        isNewUser || !!snapshot.context.isNewUser
      );
      successCallback?.(cognitoSession);
    } catch (e) {
      setOtpError(`An error occurred: Please try again after some time`);
      console.error(e);
      errorCallback?.();
    } finally {
      setVerifyingOtp(false);
    }
  };

  const bus = useBus();
  const navigate = useNavigate();

  const actor = useActor(userLoginMachine, {
    input: {
      showUserSignUpFlow: false,
      stepsToCompleteSignUp: [],
      ssoRedirectUrl,
      initialOpenPath: { pathname: location.pathname, search: location.search },
      navigate,
      reduxDispatch: dispatch,
      appConfig: {
        storeBrandingType: domainConfig?.storeBrandingType,
        brandId: domainConfig?.brandId,
        domainName: domainConfig?.domainName,
      },
    },
  });

  const [snapshot, send, actorRef] = actor;
  // Use the magic link authentication hook
  const { isLoading: submittingMagicLinkSignIn } = useMagicLinkAuth(send);

  const [signInError, setSignInError] = useState<string | undefined>(
    snapshot.context.errorMessage
  );

  // used to keep login state in sync with browser navigation
  useSyncBrowserNavigationWithUserLoginState(send);

  const clearStateForSignOut = useCallback(async () => {
    // clear all auth snapshot.in redux store
    dispatch(clearUserProfile());
    dispatch(clearRewards());
    dispatch(clearCustomerSpendByBrand());
  }, [dispatch]);

  useEffect(() => {
    (async () => {
      try {
        if (submittingMagicLinkSignIn || submittingSignIn) {
          return;
        }
        const { signedIn, userInfo } = await getUserSignedInState();
        if (signedIn) {
          AnalyticsService.track(AnalyticsTrackingEvent.AUTO_SIGN_IN, {
            userInfo,
          });
        }
      } catch (error) {
        console.error("Error during auto sign in", error);
      }
    })();
  }, [submittingMagicLinkSignIn, submittingSignIn]);

  useEffect(() => {
    const unsubscribe = Hub.listen("auth", ({ payload: { event, data } }) => {
      switch (event) {
        case "signUp":
          trackAction(AnalyticsTrackingEvent.SIGN_UP, {});
          break;
        case "signIn":
          // complete the sign in process but we need to wait for the complete event to after doing token refresh
          trackAction(AnalyticsTrackingEvent.SIGN_IN, {});
          break;
        case "signIn_Complete":
          loadCartIntoUser();
          transferVisitorActionsToUser();
          break;
        case "signOut":
          bus.emit(CLOSE_LOGIN_DRAWER);
          trackAction(AnalyticsTrackingEvent.SIGN_OUT, {});
          clearStateForSignOut();
          send({ type: "Hub.Auth.SignOut" });
          break;
        case "signIn_failure":
          trackAction(AnalyticsTrackingEvent.SIGN_IN_FAILURE, {});
          console.error("Auth Hub Event: signIn_failure", event, data);
          break;
        case "cognitoHostedUI_failure":
          console.error("Auth Hub Event: Sign in failure", data);
          break;
      }
    });

    return unsubscribe;
  }, [bus, clearStateForSignOut, send, trackAction]);

  // Authentication with phone number
  const signInWithPhone = async (
    phoneNumber: string,
    isNewUser: boolean,
    successCallback?: (isNewUser: boolean, session: any) => void
  ) => {
    try {
      const session = await AuthHelper.sendAuthOtpWithSms(
        phoneNumber,
        isNewUser
      );

      successCallback?.(isNewUser, session);
      return session;
    } catch (e: any) {
      if ((e.name || e.code) === "UserNotFoundException") {
        // Handle user not found
      }
      setSignInError(`An error occurred: Please try again after some time`);
    }
  };

  /**
   * Function signIn gets the login started. It verifies the phone number with Cognito
   */
  const signIn = async (
    phoneNumber: string,
    isUpdatePhoneFlow: boolean,
    successCallback?: (isNewUser: boolean, session: any) => void
  ) => {
    setSubmittingSignIn(true);

    try {
      if (isUpdatePhoneFlow) {
        const session = snapshot.context.session;
        const sessionId = snapshot.context.sessionId;

        if (!session || !sessionId) {
          throw new Error("Session not found");
        }

        try {
          const { status, errorMessage } = await saveNewPhoneNumber({
            session,
            sessionId,
            phoneNumber,
          });

          if (status === Status.success) {
            await signInWithPhone(phoneNumber, false, successCallback);
          } else {
            setSignInError(
              errorMessage ||
                `An error occurred: Please try again after some time`
            );
          }
        } catch (e: any) {
          setSignInError(`An error occurred: Please try again after some time`);
          console.error(e);
        }
      } else {
        try {
          // Check if we have a redirect path in the location state
          const redirectPath = location.state?.redirectPath;
          if (redirectPath) {
            send({ type: "SIGN_IN", redirectPath } as any);
          }

          await AuthHelper.signUp(phoneNumber);
          return await signInWithPhone(phoneNumber, true, successCallback);
        } catch (e: any) {
          if (e.code === "UsernameExistsException") {
            return await signInWithPhone(phoneNumber, false, successCallback);
          } else {
            throw e;
          }
        }
      }
    } finally {
      setSubmittingSignIn(false);
    }
  };

  if (!authConfig || submittingMagicLinkSignIn) {
    return <Loading fullscreen storeSpin />;
  }

  return (
    <UserLoginContext.Provider
      value={{
        actorRef: actorRef,
        snapshot: snapshot,
        send: send,
        signIn: signIn,
        submittingSignIn: submittingSignIn,
        signInError: signInError,
        setSignInError: setSignInError,
        resendCoolDown,
        verifyingOtp: verifyingOtp,
        loadingProfile: loadingProfile,
        otpError: otpError,
        setOtpError: setOtpError,
        verifyOtp: verifyOtp,
        resendOtp: resendOtp,
        clearOtpError: clearOtpError,
      }}
    >
      {children}
    </UserLoginContext.Provider>
  );
};

export type UserLoginContextType = {
  actorRef: ActorRefFrom<ActorLogicFrom<typeof userLoginMachine>>;
  snapshot: SnapshotFrom<typeof userLoginMachine>;
  send: ActorRefFrom<ActorLogicFrom<typeof userLoginMachine>>["send"];
  signIn: (
    phoneNumber: string,
    isUpdatePhoneFlow: boolean,
    successCallback?: (isNewUser: boolean, session: any) => void
  ) => Promise<void>;
  submittingSignIn: boolean;
  signInError: string | undefined;
  setSignInError: (errorMsg: string) => void;
  resendCoolDown: number;
  verifyingOtp: boolean;
  loadingProfile: boolean;
  otpError: string | null;
  setOtpError: (errorMsg: string) => void;
  clearOtpError: () => void;
  verifyOtp: (
    otp: string,
    otpSession?: any,
    successCallback?: () => void,
    errorCallback?: () => void
  ) => Promise<void>;
  resendOtp: (
    phoneNumber?: string,
    isNewUser?: boolean,
    successCallback?: (cognitoSession: any) => void,
    errorCallback?: () => void
  ) => Promise<void>;
};

export default UserLoginProvider;
